import * as React from "react";
import { cn } from "../utils/cn";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./table";
import { TableSkeleton } from "./skeletons/TableSkeleton";
import { Text } from "../primitives/typography";
import { useVirtualRows } from "../hooks/useVirtualRows";

/**
 * One column of a `DataTable`.
 *
 * `cell` is a function of the row rather than a field name, which is what lets a column render a
 * badge, a switch or a composed cell without the table knowing anything about the domain. The table
 * owns layout and states; columns own meaning.
 */
export interface DataTableColumn<T> {
    /** Stable identity for React keys and for a page to name the columns it wants. */
    key: string;
    header: React.ReactNode;
    cell: (row: T) => React.ReactNode;
    /** Applied to the body cells — alignment, width, truncation. */
    className?: string;
    headerClassName?: string;
}

export interface DataTableProps<T> {
    columns: readonly DataTableColumn<T>[];
    rows: readonly T[];
    /** Row identity. Required rather than falling back to the index, which breaks on re-sort. */
    rowKey: (row: T) => string;
    onRowClick?: (row: T) => void;
    /**
     * Trailing right-aligned cell, rendered only when supplied. Kept separate from `columns` because
     * it is the one column that is never data — it does not export to CSV or sort, and every table
     * that has one puts it last.
     */
    actions?: (row: T) => React.ReactNode;
    actionsHeader?: React.ReactNode;
    loading?: boolean;
    /** Shown when there is genuinely nothing — no rows at all. */
    empty?: React.ReactNode;
    /**
     * Shown when rows exist but the current filters match none of them. A distinct state on purpose:
     * "no members yet" and "nothing matches this search" call for different actions, and an empty
     * body under a populated search box otherwise reads as a failed load.
     */
    noMatches?: React.ReactNode;
    /** Whether anything is currently narrowed, which is what chooses between the two states above. */
    filtered?: boolean;
    /**
     * Whether a row is selected, for the tables with checkbox selection. Sets `data-state="selected"`,
     * which is what `TableRow` styles on — the same attribute those tables were setting by hand.
     */
    rowSelected?: (row: T) => boolean;
    className?: string;
    /**
     * Inline styles on the table element, merged over its own.
     *
     * For the values a class cannot carry — a computed width, a height a measurement produced. A
     * look that a page wants belongs in `className` and a look every table should have belongs in
     * the design tokens; an inline style wins over both stylesheets and the cascade, so it is the
     * last resort rather than the quick one.
     */
    style?: React.CSSProperties;
    /**
     * Classes for the scroll container around the table rather than the table itself — see
     * `TableProps`. A table dropped inside a card that already has a border and a radius turns
     * off its own with this.
     */
    wrapperClassName?: string;
    /**
     * Render only the rows on screen.
     *
     * Off by default, because most admin tables hold tens of rows and virtualizing those costs a
     * measurement pass to save nothing. Turn it on for the tables that grow without limit — the
     * users directory is 85,371 rows, and a windowed fetch still leaves every row somebody has
     * scrolled past sitting in the DOM.
     *
     * Rows keep their normal flow: the slice is padded with spacer rows above and below rather than
     * absolutely positioned, because a `<tr>` taken out of flow collapses the column widths that
     * make a table a table.
     */
    virtualize?: boolean;
}

/**
 * A table, given columns and rows.
 *
 * Exists because five admin tables had each written out the same `Table`/`TableHeader`/`TableBody`
 * scaffold, and each had solved the empty and no-match states differently or not at all. What varies
 * between them is genuinely only the columns — so those are data, and this is the one place the
 * scaffold, the loading skeleton, the two empty states and the row-click affordance live.
 *
 * Specialized tables (`UserTable`, `OrgTable`) compose this with a library of column definitions for
 * their domain; pages pick the columns they want. That keeps domain knowledge out of here without
 * making every page re-derive the shell.
 */
const DataTable = <T,>({
    columns,
    rows,
    rowKey,
    onRowClick,
    actions,
    actionsHeader = "Actions",
    loading = false,
    empty,
    noMatches,
    filtered = false,
    rowSelected,
    className,
    style,
    wrapperClassName,
    virtualize = false,
}: DataTableProps<T>) => {
    /*
     * Hooks run unconditionally, above the early return.
     *
     * `useVirtualRows` is called whether or not virtualization is on — a hook behind a condition
     * changes the hook order between renders, which React treats as a different component. When
     * `virtualize` is false it is handed a count of zero and costs nothing.
     */
    const { listRef, items, measureElement, offsetTop, offsetBottom } = useVirtualRows<HTMLTableElement>(
        virtualize ? rows.length : 0,
    );

    if (loading && rows.length === 0) return <TableSkeleton />;

    /*
     * The slice to render, and the empty space either side of it.
     *
     * `paddingTop`/`paddingBottom` become the heights of two spacer rows, so the scrollbar behaves
     * as though every row were present while only the visible ones exist.
     */
    // `flatMap` rather than `map`: after a filter shrinks the list, a virtual item can point past
    // the end for a frame before the virtualizer recomputes. Dropping those beats rendering a hole.
    const visible = virtualize
        ? items.flatMap((item) => {
              const row = rows[item.index];
              return row === undefined ? [] : [{ row, item }];
          })
        : null;
    const paddingTop = virtualize ? Math.max(0, offsetTop) : 0;
    const paddingBottom = virtualize ? Math.max(0, offsetBottom) : 0;

    // Nothing at all, and nothing narrowing — there is no table worth rendering, only an invitation
    // to add the first row.
    if (rows.length === 0 && !filtered && empty) return <>{empty}</>;

    const columnCount = columns.length + (actions ? 1 : 0);

    return (
        <Table className={cn("w-full", className)} style={style} wrapperClassName={wrapperClassName} ref={listRef}>
            <TableHeader>
                <TableRow>
                    {columns.map((column) => (
                        <TableHead key={column.key} className={column.headerClassName}>
                            {column.header}
                        </TableHead>
                    ))}
                    {actions && <TableHead className="text-right">{actionsHeader}</TableHead>}
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={columnCount}>
                            <Text muted className="py-6 text-center text-sm">
                                {noMatches ?? "Nothing matches these filters."}
                            </Text>
                        </TableCell>
                    </TableRow>
                )}
                {/*
                 * Spacer rows stand in for everything above and below the rendered slice, so the
                 * page is as tall as the full list and the scrollbar means what it says. Height on
                 * the `<tr>` rather than a transform: a table row moved out of flow takes the
                 * column widths with it.
                 */}
                {paddingTop > 0 && (
                    <TableRow aria-hidden>
                        <TableCell colSpan={columnCount} style={{ height: paddingTop, padding: 0, border: 0 }} />
                    </TableRow>
                )}
                {(visible ?? rows.map((row) => ({ row, item: null as null | { index: number } }))).map(
                    ({ row, item }) => (
                        <TableRow
                            key={rowKey(row)}
                            clickable={!!onRowClick}
                            data-state={rowSelected?.(row) ? "selected" : undefined}
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                            // Reports its real height back, which is what keeps a two-line row and a
                            // three-line row from both being treated as the estimate.
                            {...(item ? { "data-index": item.index, ref: measureElement } : {})}
                        >
                            {columns.map((column) => (
                                <TableCell key={column.key} className={column.className}>
                                    {column.cell(row)}
                                </TableCell>
                            ))}
                            {actions && (
                                <TableCell
                                    className="text-right"
                                    // A click on an action is not a click on the row. Without this, Edit
                                    // also navigated to the row's detail page.
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {actions(row)}
                                </TableCell>
                            )}
                        </TableRow>
                    ),
                )}
                {paddingBottom > 0 && (
                    <TableRow aria-hidden>
                        <TableCell colSpan={columnCount} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
};

export { DataTable };
